// src/services/storeService.js
import { api } from "./interceptors";

const RADIUS = 15;
/**
 * Store Service - Handles all store-related API calls
 */
const storeService = {
  /**
   * Get nearby stores by latitude and longitude
   * @param {number} latitude
   * @param {number} longitude
   * @param {number} page - Page number for pagination (default: 1)
   * @returns {Promise<Array>} - Returns nearby stores data as array
   */
  getNearbyStores: async (latitude, longitude, page = 1) => {
    try {
      const response = await api.get("/stores/nearby/", {
        params: {
          latitude: latitude.toFixed(8),
          longitude: longitude.toFixed(8),
          page,
          page_size: 1000,
          radius_km: RADIUS
        },
      });

      // Handle the new API response format: { success: true, data: { data: [...] } }
      const responseData = response.data;
      if (
        responseData &&
        responseData.success &&
        responseData.data &&
        Array.isArray(responseData.data.data)
      ) {
        return responseData.data.data;
      } else {
        // Fallback for direct array response
        return responseData;
      }
    } catch (error) {
      console.error("Error fetching stores from API:", error);
    }
  },

  /**
   * Get user's favorite stores with location for distance calculation
   * @param {number} latitude - User's latitude for distance calculation
   * @param {number} longitude - User's longitude for distance calculation
   * @param {number} page - Page number for pagination (default: 1)
   * @returns {Promise<Object>} - Returns favorites data with pagination
   */
  getFavoriteStores: async (latitude = null, longitude = null, page = 1) => {
    try {
      const params = { page };

      // Add latitude and longitude if provided for distance calculation
      if (latitude !== null && longitude !== null) {
        params.latitude = latitude.toFixed(8);
        params.longitude = longitude.toFixed(8);
        params.radius_km = RADIUS;
      }



      const response = await api.get("/stores/favorites/", {
        params,
      });
      console.log("Favorites response:", response.data);

      // Handle the API response format: { success: true, data: [...], pagination: {...} }
      const responseData = response.data;
      if (
        responseData &&
        responseData.success &&
        Array.isArray(responseData.data)
      ) {
        // Transform the favorites data to match the store format
        const transformedStores = responseData.data.map((favorite) => ({
          ...favorite.store,
          is_favorite: true, // Mark as favorite since it comes from favorites API
        }));

        return {
          data: transformedStores,
          pagination: responseData.pagination,
          hasMore: responseData.pagination?.has_next || false,
          next_page: responseData.pagination?.next_page || null,
          total_pages: responseData.pagination?.total_pages || 1,
        };
      } else {
        // Return empty results if no favorites
        return {
          data: [],
          pagination: {
            current_page: 1,
            total_pages: 1,
            count: 0,
            has_next: false,
            has_previous: false,
          },
          hasMore: false,
          next_page: null,
          total_pages: 1,
        };
      }
    } catch (error) {
      console.error("Error fetching favorite stores from API:", error);
      // Return empty results on error
      return {
        data: [],
        pagination: {
          current_page: 1,
          total_pages: 1,
          count: 0,
          has_next: false,
          has_previous: false,
        },
        hasMore: false,
        next_page: null,
        total_pages: 1,
      };
    }
  },

  /**
   * Check if a store is favorited by getting user's favorites and checking if store exists
   * @param {string} storeId - The store's public_id
   * @returns {Promise<boolean>} - Returns true if store is favorited
   */

  /**
   * Toggle favorite status for a store
   * @param {string} storeId - The store's public_id
   * @returns {Promise<Object>} - Returns the API response
   */
  toggleFavorite: async (storeId) => {
    try {
      const response = await api.post("/stores/favorites/toggle/", {
        store_id: storeId,
      });
      console.log("Toggle favorite response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error toggling store favorite:", error);
      throw error;
    }
  },
};

export default storeService;